import React, { useRef, useState, useEffect } from 'react';
import { Bold, Italic, Underline, Link, List, ListOrdered, Heading2, Heading3, X, Search } from 'lucide-react';
import { useLanguage } from '../utils/useLanguage';
import useActivityPath from '@/hooks/useActivityPath';

function ToolbarBtn({ onClick, title, active, children }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      title={title}
      className={`px-2 py-1 rounded text-sm font-semibold border transition-colors
        ${active
          ? 'bg-blue-700 text-white border-blue-700'
          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
        }`}
    >
      {children}
    </button>
  );
}

export default function RichTextEditor({ value, onChange, placeholder = "Start typing…", activities = [], features = [] }) {
  const { language, t } = useLanguage();
  const to = useActivityPath();
  const editorRef = useRef(null);
  const savedSelection = useRef(null);

  // Link panel state: 'none' | 'external' | 'internal'
  const [linkMode, setLinkMode] = useState('none');
  const [linkUrl, setLinkUrl] = useState('');
  const [activitySearch, setActivitySearch] = useState('');
  const [featureSearch, setFeatureSearch] = useState('');

  // Set initial content on mount
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = value || '';
    }
  }, []);

  // Sync external value changes (e.g. when switching between records)
  useEffect(() => {
    if (editorRef.current && document.activeElement !== editorRef.current) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const emitChange = () => {
    if (onChange && editorRef.current) onChange(editorRef.current.innerHTML);
  };

  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    emitChange();
  };

  const formatBlock = (tag) => {
    editorRef.current?.focus();
    document.execCommand('formatBlock', false, tag);
    emitChange();
  };

  const isActive = (cmd) => { try { return document.queryCommandState(cmd); } catch { return false; } };

  const isBlock = (tag) => {
    const sel = window.getSelection();
    if (!sel?.rangeCount) return false;
    let node = sel.anchorNode;
    while (node && node !== editorRef.current) {
      if (node.nodeName === tag.toUpperCase()) return true;
      node = node.parentNode;
    }
    return false;
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel?.rangeCount > 0) savedSelection.current = sel.getRangeAt(0).cloneRange();
  };

  const restoreSelection = () => {
    const sel = window.getSelection();
    if (savedSelection.current && sel) {
      sel.removeAllRanges();
      sel.addRange(savedSelection.current);
    }
  };

  const insertLink = (url, label = null) => {
    if (!url.trim()) return;
    restoreSelection();
    editorRef.current?.focus();
    const sel = window.getSelection();
    const isInternal = url.startsWith('/');
    const finalUrl = isInternal || url.startsWith('http') ? url : `https://${url}`;
    if (sel?.toString().length > 0) {
      document.execCommand('createLink', false, finalUrl);
      editorRef.current?.querySelectorAll(`a[href="${finalUrl}"]`).forEach(l => {
        l.setAttribute('target', '_blank');
        l.setAttribute('rel', 'noopener noreferrer');
      });
    } else {
      const text = label || finalUrl;
      document.execCommand('insertHTML', false, `<a href="${finalUrl}" target="_blank" rel="noopener noreferrer">${text}</a>`);
    }
    setLinkUrl('');
    setActivitySearch('');
    setFeatureSearch('');
    setLinkMode('none');
    emitChange();
  };

  const openLinkPanel = (mode) => {
    saveSelection();
    setLinkMode(prev => prev === mode ? 'none' : mode);
    setLinkUrl('');
    setActivitySearch('');
    setFeatureSearch('');
  };

  const filteredActivities = activities.filter(a => {
    const q = activitySearch.toLowerCase();
    const title = language === 'en' ? (a.teaser_title_en || a.teaser_title_de || '') : (a.teaser_title_de || '');
    return title.toLowerCase().includes(q) || (a.slug || '').includes(q);
  }).slice(0, 10);

  const getActivityTitle = (a) =>
    language === 'en' ? (a.teaser_title_en || a.teaser_title_de || a.slug) : (a.teaser_title_de || a.slug);

  const getActivityUrl = (a) => to(a);

  const filteredFeatures = features.filter(f => {
    const q = featureSearch.toLowerCase();
    const title = language === 'en' ? (f.title_en || f.title_de || '') : (f.title_de || '');
    return title.toLowerCase().includes(q) || (f.slug || '').includes(q);
  }).slice(0, 10);

  const getFeatureTitle = (f) =>
    language === 'en' ? (f.title_en || f.title_de || f.slug) : (f.title_de || f.slug);

  // Feature/update detail pages live at /updates/<slug> (see App.jsx route).
  const getFeatureUrl = (f) => `/updates/${f.slug}`;

  const hasInternalTargets = activities.length > 0 || features.length > 0;

  return (
    <div className="rounded-lg border border-gray-300 overflow-hidden text-sm">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1.5 px-3 py-2 bg-gray-50 border-b border-gray-300">
        <ToolbarBtn onClick={() => formatBlock('h2')} title="Heading 2" active={isBlock('H2')}>
          <Heading2 className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => formatBlock('h3')} title="Heading 3" active={isBlock('H3')}>
          <Heading3 className="w-4 h-4" />
        </ToolbarBtn>

        <div className="w-px h-5 bg-gray-300 mx-0.5" />

        <ToolbarBtn onClick={() => exec('bold')} title="Bold" active={isActive('bold')}>
          <Bold className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => exec('italic')} title="Italic" active={isActive('italic')}>
          <Italic className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => exec('underline')} title="Underline" active={isActive('underline')}>
          <Underline className="w-4 h-4" />
        </ToolbarBtn>

        <div className="w-px h-5 bg-gray-300 mx-0.5" />

        <ToolbarBtn onClick={() => exec('insertUnorderedList')} title="Bullet list">
          <List className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => exec('insertOrderedList')} title="Numbered list">
          <ListOrdered className="w-4 h-4" />
        </ToolbarBtn>

        <div className="w-px h-5 bg-gray-300 mx-0.5" />

        <ToolbarBtn onClick={() => openLinkPanel('external')} title={t('Externer Link', 'External link')} active={linkMode === 'external'}>
          <Link className="w-4 h-4" />
        </ToolbarBtn>
        {hasInternalTargets && (
          <ToolbarBtn onClick={() => openLinkPanel('internal')} title={t('Interner Link', 'Internal link')} active={linkMode === 'internal'}>
            <Search className="w-4 h-4" />
          </ToolbarBtn>
        )}
        <ToolbarBtn onClick={() => exec('removeFormat')} title="Clear formatting">
          <X className="w-4 h-4" />
        </ToolbarBtn>
      </div>

      {/* External link panel */}
      {linkMode === 'external' && (
        <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border-b border-blue-200">
          <input
            autoFocus
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') insertLink(linkUrl);
              if (e.key === 'Escape') setLinkMode('none');
            }}
            placeholder="https://example.com"
            className="flex-1 text-sm px-2 py-1 rounded border border-blue-300 outline-none focus:ring-1 focus:ring-blue-400"
          />
          <button
            type="button"
            onClick={() => insertLink(linkUrl)}
            className="px-3 py-1 bg-blue-600 text-white text-xs font-semibold rounded hover:bg-blue-700"
          >
            {t('Einfügen', 'Insert')}
          </button>
          <button
            type="button"
            onClick={() => setLinkMode('none')}
            className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100"
          >
            {t('Abbrechen', 'Cancel')}
          </button>
        </div>
      )}

      {/* Internal link panel */}
      {linkMode === 'internal' && (
        <div className="px-3 py-2 bg-emerald-50 border-b border-emerald-200 space-y-3">
          {activities.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-gray-500">{t('Aktivitäten', 'Activities')}</p>
              <input
                autoFocus
                value={activitySearch}
                onChange={(e) => setActivitySearch(e.target.value)}
                placeholder={t('Aktivität suchen…', 'Search activities…')}
                className="w-full text-sm px-2 py-1 rounded border border-emerald-300 outline-none focus:ring-1 focus:ring-emerald-400"
              />
              <div className="max-h-32 overflow-y-auto space-y-0.5">
                {filteredActivities.length === 0 && (
                  <p className="text-xs text-gray-400 py-1">{t('Keine Aktivitäten gefunden', 'No activities found')}</p>
                )}
                {filteredActivities.map(a => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => insertLink(getActivityUrl(a), getActivityTitle(a))}
                    className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-emerald-100 text-gray-700 truncate"
                  >
                    {getActivityTitle(a)}
                    <span className="ml-1 text-gray-400">/{a.slug}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {features.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-gray-500">{t('Funktionen', 'Features')}</p>
              <input
                value={featureSearch}
                onChange={(e) => setFeatureSearch(e.target.value)}
                placeholder={t('Funktion suchen…', 'Search features…')}
                className="w-full text-sm px-2 py-1 rounded border border-emerald-300 outline-none focus:ring-1 focus:ring-emerald-400"
              />
              <div className="max-h-32 overflow-y-auto space-y-0.5">
                {filteredFeatures.length === 0 && (
                  <p className="text-xs text-gray-400 py-1">{t('Keine Funktionen gefunden', 'No features found')}</p>
                )}
                {filteredFeatures.map(f => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => insertLink(getFeatureUrl(f), getFeatureTitle(f))}
                    className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-emerald-100 text-gray-700 truncate"
                  >
                    {getFeatureTitle(f)}
                    <span className="ml-1 text-gray-400">/updates/{f.slug}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={() => setLinkMode('none')}
            className="text-xs text-gray-400 hover:text-gray-600"
          >
            {t('Abbrechen', 'Cancel')}
          </button>
        </div>
      )}

      {/* Editable area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={emitChange}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && (isBlock('H2') || isBlock('H3'))) {
            e.preventDefault();
            document.execCommand('insertParagraph');
            document.execCommand('formatBlock', false, 'p');
            emitChange();
          }
        }}
        data-placeholder={placeholder}
        className="rich-editor min-h-[160px] px-3.5 py-3 bg-white outline-none text-base leading-relaxed text-gray-900"
      />

      <style>{`
        .rich-editor:empty:before { content: attr(data-placeholder); color: #9ca3af; pointer-events: none; }
        .rich-editor h2 { font-size: 1.4em; font-weight: 700; margin: 0.8em 0 0.3em; }
        .rich-editor h3 { font-size: 1.15em; font-weight: 600; margin: 0.7em 0 0.2em; }
        .rich-editor a { color: #2563eb; text-decoration: underline; }
        .rich-editor ul { list-style: disc; padding-left: 1.5em; margin: 0.4em 0; }
        .rich-editor ol { list-style: decimal; padding-left: 1.5em; margin: 0.4em 0; }
        .rich-editor p { margin: 0.3em 0; }
        .rich-editor strong, .rich-editor b { font-weight: 700; }
      `}</style>
    </div>
  );
}